import {ApiError} from "@devour/client";

/**
 * Is this an error body that came off the wire, as opposed to a thrown Error?
 *
 * CRITICAL: Error instances must be rejected here.
 *
 * The generated client throws `ResponseError` and `FetchError`, both of which
 * extend Error. Error's constructor assigns `message` as an OWN property, so a
 * check of "does it have a string `message`" returns true for them — and every
 * caller then treated the wrapper as a valid API error and rendered its
 * message. Users saw the literal string "Response returned an error code"
 * (ResponseError's constant) in place of the real backend message, and the
 * parsing that would have read the actual body was never reached.
 *
 * A genuine ApiError is a plain object parsed from JSON: {type, level, message,
 * httpStatus, ...}. It is never an Error instance. The `name` check is a
 * belt-and-braces guard for cross-realm cases (iframes) where `instanceof`
 * can fail.
 *
 * Deliberately still permissive about `type`: some call sites throw plain
 * `{message: "..."}` objects and rely on those rendering as-is.
 */
export function isAPIError(err: any): err is ApiError {

    if (err == null || typeof err !== "object") {
        return false;
    }

    if (err instanceof Error || err.name === "ResponseError" || err.name === "FetchError") {
        return false;
    }

    if (!err.hasOwnProperty("message") || typeof err.message !== "string") {
        return false;
    }

    if (err.hasOwnProperty("errors") && err.errors != null) {

        if (!Array.isArray(err.errors) || !err.errors.every(isAPIError)) {
            return false;
        }

    }

    return true;
}
